import React, { useState } from 'react';
import { format, isToday, differenceInHours, differenceInDays, differenceInMinutes } from 'date-fns';
import { Copy } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface MessageTimestampProps {
  timestamp: number;
  type?: "system" | "user";
}

const MessageTimestamp: React.FC<MessageTimestampProps> = ({ timestamp, type }) => {
  const [isHovered, setIsHovered] = useState(false);
  const { toast } = useToast();
  const date = new Date(timestamp);
  const now = new Date();
  
  const formatTimestamp = () => {
    if (isToday(date)) {
      // For today: "2:34pm (2h ago)"
      const timeStr = format(date, 'h:mmaaa');
      const minutesAgo = differenceInMinutes(now, date);
      const hoursAgo = differenceInHours(now, date);
      
      let relativeTime;
      if (minutesAgo < 60) {
        relativeTime = minutesAgo <= 1 ? 'just now' : `${minutesAgo}m ago`;
      } else {
        relativeTime = `${hoursAgo}h ago`;
      }
      
      return `${timeStr} (${relativeTime})`;
    } else {
      // For older: "2:34pm on June 13 (2mo ago)"
      const timeStr = format(date, 'h:mmaaa');
      const dateStr = format(date, 'MMMM d');
      const daysAgo = differenceInDays(now, date);
      
      let relativeTime;
      if (daysAgo < 30) {
        relativeTime = `${daysAgo}d ago`;
      } else if (daysAgo < 365) {
        const monthsAgo = Math.floor(daysAgo / 30);
        relativeTime = `${monthsAgo}mo ago`;
      } else {
        const yearsAgo = Math.floor(daysAgo / 365);
        relativeTime = `${yearsAgo}y ago`;
      }
      
      return `${timeStr} on ${dateStr} (${relativeTime})`;
    }
  };

  const formatHoverTimestamp = () => {
    // Format as "HH:MM:SS on Aug 13"
    const timeStr = format(date, 'HH:mm:ss');
    const dateStr = format(date, 'MMM d');
    return `${timeStr} on ${dateStr}`;
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(formatHoverTimestamp());
      toast({
        title: "Copied to clipboard",
        description: "Timestamp copied to your clipboard.",
      });
    } catch (error) {
      console.error('Failed to copy to clipboard:', error);
      toast({
        title: "Copy failed",
        description: "Failed to copy to clipboard. Please try again.",
        variant: "destructive",
      });
    }
  };

  const isUserMessage = type === "user";

  return (
    <div 
      className={`text-xs text-timestamp-foreground mt-2 flex items-center gap-2 ${isUserMessage ? 'flex-row-reverse' : ''}`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <span>
        {isHovered ? formatHoverTimestamp() : formatTimestamp()}
      </span>
      {isHovered && (
        <button
          type="button"
          onClick={handleCopy}
          className="opacity-70 hover:opacity-100 transition-opacity"
          aria-label="Copy timestamp"
        >
          <Copy className="h-3 w-3" />
        </button>
      )}
    </div>
  );
};

export default MessageTimestamp;